import { useCallback, useRef, useState } from 'react';
import { X, Check, Triangle } from 'lucide-react';
import GeogebraElement from './GeogebraElement';

const LETTRES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Modal GeoGebra pour éditer un choix QCM.
 * Utilisé par QuestionForm et AddQuestion.
 *
 * @param {number|null} index - Index du choix en édition (null = fermé)
 * @param {Array} responses - State des réponses QCM
 * @param {Function} setResponses - Setter des réponses
 * @param {Function} onClose - Fermer le modal (→ setGgbModalIndex(null))
 */
const GeogebraChoixModal = ({ index, responses, setResponses, onClose }) => {
    if (index === null) return null;

    const config = responses[index]?.element?.config || {};

    return (
        <div
            className="fixed inset-0 z-[10010] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[950px] max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-sky-50 to-cyan-50">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-sky-100 rounded-lg flex items-center justify-center">
                            <Triangle size={14} className="text-sky-600" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-800">Figure GeoGebra</h3>
                            <p className="text-[10px] text-gray-400">Réponse {LETTRES[index]} — sauvegarde automatique</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="flex-1 overflow-auto p-4">
                    <GeogebraElement
                        config={config}
                        onConfigChange={(cfg) => {
                            setResponses(prev => {
                                const next = [...prev];
                                next[index] = { ...next[index], element: { ...next[index].element, config: cfg } };
                                return next;
                            });
                        }}
                    />
                </div>
                <div className="flex items-center justify-end px-5 py-3 border-t border-gray-100 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-sky-500 text-white hover:bg-sky-600 transition shadow-sm"
                    >
                        <Check size={14} strokeWidth={3} />
                        Terminé
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Hook pour gérer le state du modal GeoGebra (index + ref sync).
 * @returns {[number|null, Function]} [ggbModalIndex, setGgbModalIndex]
 */
export const useGeogebraModalState = () => {
    const [ggbModalIndex, _set] = useState(null);
    const ref = useRef(null);
    const setGgbModalIndex = useCallback((val) => {
        const next = typeof val === 'function' ? val(ref.current) : val;
        ref.current = next;
        _set(next);
    }, []);
    return [ggbModalIndex, setGgbModalIndex];
};

export default GeogebraChoixModal;
